import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { formatDateTimeBR } from '../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function ConciliacaoTip({ tipConfigurada }) {
  const [info, setInfo] = useState(null);
  const [verLista, setVerLista] = useState(false);
  const rodavaRef = useRef(false);

  const carregar = useCallback(() => axios.get(`${API_URL}/api/operadora/conciliacao`, { withCredentials: true })
    .then(r => {
      if (rodavaRef.current && !r.data.rodando) {
        if (r.data.erro) toast.error(`Conferência falhou: ${r.data.erro}`);
        else toast.success(`Conferência concluída: ${r.data.criadas ?? 0} ativação(ões) importada(s), ${r.data.migrados ?? 0} chip(s) passaram para a Tip`);
      }
      rodavaRef.current = !!r.data.rodando;
      setInfo(r.data);
    }).catch(() => {}), []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (!info?.rodando) return undefined;
    const t = setInterval(carregar, 5000);
    return () => clearInterval(t);
  }, [info?.rodando, carregar]);

  const executar = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/operadora/conciliacao/executar`, {}, { withCredentials: true });
      toast.success(r.data.message);
      rodavaRef.current = true;
      setInfo(i => ({ ...(i || {}), rodando: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao iniciar a conferência');
    }
  };

  const semConf = info?.sem_confirmacao || [];
  return (
    <div className="dashboard-card space-y-3" data-testid="conciliacao-tip">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="section-title mb-1">Ativações feitas fora do sistema</h2>
          <p className="text-xs text-zinc-400">A cada hora o sistema confere a Tip: traz os chips dela e cria cliente e linha para o que foi ativado no painel da Tá ou da Tip.</p>
        </div>
        <Button onClick={executar} disabled={!tipConfigurada || info?.rodando} className="btn-primary" data-testid="btn-conferir-tip">
          <RefreshCw className={`w-4 h-4 mr-2 ${info?.rodando ? 'animate-spin' : ''}`} /> {info?.rodando ? 'Conferindo...' : 'Conferir agora'}
        </Button>
      </div>
      {info?.erro && <p className="text-sm text-red-400" data-testid="conciliacao-erro">Última tentativa falhou: {info.erro}</p>}
      {info?.ultima_execucao ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="conciliacao-resumo">
          <Item rotulo="Última conferência" valor={formatDateTimeBR(info.ultima_execucao)} />
          <Item rotulo="Ativações importadas" valor={info.criadas ?? 0} testId="conciliacao-criadas" />
          <Item rotulo="Chips que passaram para a Tip" valor={info.migrados ?? 0} testId="conciliacao-migrados" />
          <Item rotulo="Aguardando confirmação" valor={info.sem_confirmacao_total ?? 0} testId="conciliacao-sem-confirmacao" />
        </div>
      ) : <p className="text-sm text-zinc-400">Ainda não foi feita nenhuma conferência.</p>}
      {semConf.length > 0 && (
        <div className="text-sm">
          <button className="text-amber-300 flex items-center gap-1" onClick={() => setVerLista(v => !v)} data-testid="btn-ver-sem-confirmacao">
            <AlertTriangle className="w-4 h-4" /> {info.sem_confirmacao_total} contrato(s) aparecem como em uso na Tip, mas o chip ainda está disponível lá. Não foram importados.
          </button>
          {verLista && (
            <ul className="mt-2 space-y-1 text-xs text-zinc-300 max-h-48 overflow-auto" data-testid="lista-sem-confirmacao">
              {semConf.map(s => <li key={s.iccid} className="font-mono">{s.numero} · {s.nome} · chip {s.iccid} ({s.status_chip})</li>)}
            </ul>
          )}
        </div>
      )}
      {info?.ultima_execucao && !semConf.length && !info?.erro && (
        <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Tudo conferido.</p>
      )}
    </div>
  );
}

function Item({ rotulo, valor, testId }) {
  return (
    <div className="p-2 bg-zinc-800/50 rounded" data-testid={testId}>
      <p className="text-xs text-zinc-400">{rotulo}</p>
      <p className="font-mono text-zinc-100">{valor}</p>
    </div>
  );
}
